import {yupResolver} from "@hookform/resolvers/yup";
import {
    CheckCircleOutlineRounded,
    ErrorOutlineOutlined, InfoOutlined,
    KeyboardArrowLeft,
    KeyboardArrowRight, LockOutlined
} from '@mui/icons-material';
import {Box, Button, Divider, Grid, MobileStepper, Typography} from "@mui/material";
import {useEffect, useRef, useState} from "react";
import {FieldValues, useForm} from "react-hook-form";
import {Link, useHistory} from "react-router-dom";
import {toast} from "react-toastify";
import {useRecoilValue} from "recoil";
import {CoreImportService, MultiPageImportData} from '../../../../Component/Api/Import/CoreImportService';
import {
    VerificationWorkflowService
} from "../../../../Component/Api/VerificationAgency/VerificationWorkflow/VerificationWorkflowService";
import AppFileDropzone from "../../../../Component/Common/Components/FormValidationInputs/AppFileDropZone";
import {IntermediateGlobalLoading} from "../../../../Component/Common/Components/Loading/IntermediateGlobalLoading";
import {MDButton} from "../../../../Component/Common/Components/MDButton";
import {ImportProgressBarSummarize} from "../../../../Component/Common/Components/ProgressBar/ImportProgressSummarize";
import {Tooltip} from "../../../../Component/Common/Components/Tooltip/Tooltip";
import {TooltipLoadingButton} from "../../../../Component/Common/Components/Tooltip/TooltipLoadingButton";
import {ExportKeyValue} from "../../../../Component/Model/Dto/Upload";
import {
    ImportManagement
} from "../../../../Component/Model/Dto/VerificationAgency/Client/ImportManagement/ImportManagement";
import {ImportActions} from "../../../../Component/Model/Enum/BEE123Enums";
import {BBBEEScorecardAtom} from "../../../../State/BBBEEScorecard.Atom";
import {UserAtom} from "../../../../State/user.Atom";
import {MultiPageImportValidationSchema} from "./MultiPageImportValidationSchema";

import QueryBuilderOutlinedIcon from '@mui/icons-material/QueryBuilderOutlined';
import {
    VerificationDataPageSubmissionService
} from "../../../../Component/Api/VerificationAgency/VerificationDataPageSubmission/VerificationDataPageSubmissionService";
import AppMultiSelectWithDisabledEntries
    , {
    IDropDownDisabledOptions
} from "../../../../Component/Common/Components/FormValidationInputs/AppMultiSelectWithDisabledEntries";
import {useSignalRConnection} from "../../../../Hooks/useSignalRConnection";

export interface ImportResult {
    numberOfRowsCorrect: number;
    numberOfRowsFailed: number;
    log: any[];
    wasSuccessful: boolean;
    fileName?: string;
    importManagementTrackerId: number;
    additionalInfo: Record<number, string>;
    errorLogUrl: string | null;
}

export const MultiPageImport = () => {

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isImporting, setIsImporting] = useState<boolean>(false);
    const [isImportInProgressForScorecard, setIsImportInProgressForScorecard] = useState<boolean>(false);
    const [dataPagesOptions, setDataPagesOptions] = useState<IDropDownDisabledOptions[]>([]);
    const [hasAccess, setHasAccess] = useState<boolean>(true);
    const [importResult, setImportResult] = useState<ImportResult>();
    const scorecard = useRecoilValue(BBBEEScorecardAtom);
    const history = useHistory();
    const currentUser = useRecoilValue(UserAtom);
    const [importDetailsId, setImportDetailsId] = useState<number>();
    const [progressData, setProgressData] = useState<{ [key: string]: ImportManagement.ImportDetailSheetDto }>();
    const [isFileStructureCorrect, setIsFileStructureCorrect] = useState<boolean>(false);
    const [canProcessFile, setCanProcessFile] = useState<boolean>(false);
    const iconWidth = 45;
    const iconHeight = 45;
    const maxTiles = 8;
    const [activeStep, setActiveStep] = useState(0);
    const [maxSteps, setMaxSteps] = useState<number>(0);
    const [useSignalR, setUseSignalR] = useState<boolean>(false);
    const hub = useSignalRConnection("ImportHub");
    const {control, handleSubmit, setValue, reset} = useForm({
        mode: 'all',
        resolver: yupResolver(MultiPageImportValidationSchema)
    });

    const importDataRef = useRef<HTMLFormElement>(null);
    const dataValidationRef = useRef<HTMLDivElement>(null);
    const inProgressRef = useRef<HTMLDivElement>(null);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            await Promise.all([checkForInProgressImport(), getScorecardAndUserAllowedDataPages()]);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (!isLoading && isImportInProgressForScorecard) {
            inProgressRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isLoading, isImportInProgressForScorecard, importDetailsId])

    useEffect(() => {
        if(scorecard.scorecardId)
        {
            fetchData();
        }else
        {
            history.push('/home');
        }

    }, []);

    useEffect(() => {
        if (!useSignalR || !importDetailsId) return;

        (async () => {
            try {
                if (!hub.isConnected) {
                    await hub.start();
                }

                hub.on(`excelImportProgress-${scorecard.scorecardId}-${importDetailsId}`, (data: any) => {
                    const {ImportTypeId, progress} = data;
                    setProgressData((prevData) => ({
                        ...prevData, [ImportTypeId]: {
                            ...prevData![ImportTypeId],
                            progressPercentage: progress
                        }
                    }));
                });
                hub.on(`excelImportFailed-${scorecard.scorecardId}-${importDetailsId}`, (data: any) => {
                    const {ImportTypeId, NumberOfRowsCorrect, NumberOfRowsFailed, Log, ErrorBlobUrl} = data;
                    setProgressData((prevData) => ({
                        ...prevData,
                        [ImportTypeId]: {
                            ...prevData![ImportTypeId],
                            progressPercentage: 0,
                            importSuccessful: false,
                            numberOfRowsCorrect: NumberOfRowsCorrect,
                            numberOfRowsFailed: NumberOfRowsFailed,
                            log: Log,
                            errorBlobUrl: ErrorBlobUrl
                        }
                    }));
                });
                hub.on(`excelImportSuccess-${scorecard.scorecardId}-${importDetailsId}`, (data: any) => {
                    const {ImportTypeId, NumberOfRowsCorrect, NumberOfRowsFailed, Log} = data;
                    setProgressData((prevData) => ({
                        ...prevData,
                        [ImportTypeId]: {
                            ...prevData![ImportTypeId],
                            progressPercentage: 100,
                            importSuccessful: true,
                            numberOfRowsCorrect: NumberOfRowsCorrect,
                            numberOfRowsFailed: NumberOfRowsFailed,
                            log: Log
                        }
                    }));
                });
                hub.on(`excelImportComplete-${scorecard.scorecardId}`, () => {
                    setIsImportInProgressForScorecard(false);
                });
                hub.on(`excelImportFailed-${scorecard.scorecardId}`, () => {
                    toast.error('Excel import Failed. Please try again');
                    setIsImportInProgressForScorecard(false);
                });
            } catch (error) {
                console.error("SignalR connection failed:", error);
            }
        })();
    }, [useSignalR, importDetailsId]);

    const getScorecardAndUserAllowedDataPages = async () => {
        const result = await VerificationDataPageSubmissionService.GetDataPageLockMetaForImport(scorecard.scorecardId);

        setHasAccess(result.data.length > 0);
        const pendingSubmissions = currentUser.hasAuditRole ?  "- pending actions" : "- pending actions from your VA";

        const dataPageDetails = result.data.map(item => ({
            id: item.dataImportTypeId,
            value: item.dataImportName,
            isDisabled: item.isLocked,
            disabledMessage: item.isLocked ? `Data Page is locked ${(item.hasDataPageSubmission ? pendingSubmissions : "")}`: ""
        }) as IDropDownDisabledOptions)
        setDataPagesOptions(dataPageDetails);

    }

    const checkForInProgressImport = async () => {
        const result = await VerificationWorkflowService.IsImportInProgressForScorecard(scorecard.scorecardId);
        if (!!result.data) {
            setUseSignalR(true);
            setIsImportInProgressForScorecard(true);
            setCanProcessFile(true);
            setIsFileStructureCorrect(true);
            setImportDetailsId(result.data);
        }
    }

    useEffect(() => {
        if (!!importDetailsId) {
            fetchImportDetails(importDetailsId);
        }
    }, [importDetailsId]);

    const fetchImportDetails = async (importDetailsId: number) => {
        try {
            const result = await VerificationWorkflowService.GetImportDetails(importDetailsId);
            result.data?.progressSheets?.map(x => {
                setProgressData((prevData) => ({
                    ...prevData,
                    [x.importTypeId]: { ...x }
                }));
            }
            );
        }
        catch
        (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (!!progressData) {
            const keys = Object.keys(progressData);
            setMaxSteps(Math.ceil(keys.length / maxTiles));
        }
    }, [progressData]);

    const handleDataSubmit = async (data: FieldValues) => {
        dataValidationRef.current?.scrollIntoView({ behavior: 'smooth' });
        setIsImporting(true);
        setProgressData(undefined);
        try {
            const parameters: ExportKeyValue[] = [];
            const request: MultiPageImportData = {
                dataImportTypes: data.selectedImportTypes,
                scorecardId: scorecard.scorecardId,
                importAction: ImportActions.Overwrite,
                organisationId: null,
                file: data.file,
                parameters: parameters,
                scorecardType: scorecard.scorecardTypeId
            };

            var filteredDataPages = dataPagesOptions.filter(page => data.selectedImportTypes.includes(page.id));
            filteredDataPages.forEach(page => {
                setProgressData(prevData => ({
                    ...prevData,
                    [parseInt(page.id.toString())]: {
                        importTypeId: parseInt(page.id.toString()),
                        dataPageName: page.value,
                        progressPercentage: 0,
                        importSuccessful: true,
                        numberOfRowsFailed: 0,
                        numberOfRowsCorrect: 0
                    }
                }));
            });
            const result = await CoreImportService.ImportMultiPageExcelFile(request);
            if (result.data.wasSuccessful) {
                setUseSignalR(true);
                setIsImportInProgressForScorecard(true);
                setCanProcessFile(true);
                setIsFileStructureCorrect(true);
                setImportDetailsId(result.data.importManagementTrackerId);
            }
            else {
                const hasInvalidSheets = result.data.log.some(s => s.includes('sheet'));
                setCanProcessFile(hasInvalidSheets);
                setIsFileStructureCorrect(!hasInvalidSheets);
                setImportResult(result.data);
            }
        } catch (error) {
            console.log(error);
            setIsImporting(false);
            toast.error('Import Failed');
        }
        finally {
            setIsImporting(false);
        }
    }

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const renderItemsForStep = (stepIndex: number) => {
        if (!!progressData) {
            const keys = Object.keys(progressData);
            const startIndex = stepIndex * maxTiles;
            const endIndex = Math.min(startIndex + maxTiles, keys.length);

            return keys.slice(startIndex, endIndex).map(key => {
                const sheet = progressData[key];
                return (
                    <ImportProgressBarSummarize
                        key={key}
                        value={sheet.progressPercentage ?? 0}
                        iconHeight={iconHeight}
                        iconWidth={iconWidth}
                        progressData={{
                            importTypeId: sheet.importTypeId,
                            importName: sheet.dataPageName,
                            numberOfRowsCorrect: sheet.numberOfRowsCorrect,
                            numberOfRowsFailed: sheet.numberOfRowsFailed,
                            isSuccess: sheet.importSuccessful,
                        }} />
                );
            });
        }
    };

    const reselectSuccessful = (validSheets: number[]) => {
        setValue('selectedImportTypes', validSheets);
        importDataRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const clearImport = () => {
        importDataRef.current?.scrollIntoView({ behavior: 'smooth' });

        setTimeout(() => {
            reset();
            setProgressData(undefined);
            setImportResult(undefined);
        }, 10);
    }

    const defaultInformation = () => {
        return (
            <Grid item xs={12} style={{ backgroundColor: 'white' }}>
                <Box sx={{ height: '100%', width: '100%', padding: "20px 15px", display: 'flex', flexDirection: "row", alignItems: 'center' }}>
                    <QueryBuilderOutlinedIcon color="secondary" style={{ width: 45, height: 45, marginRight: '10px' }} />
                    <Typography variant="body2" color="secondary" >
                        Select a file to import.
                    </Typography>
                </Box>
            </Grid>
        );
    };

    if (isLoading) {
        return <IntermediateGlobalLoading />;
    };
    return (
        <>
            {!hasAccess && !isLoading &&
                <Box style={{
                    position: 'absolute',
                    left: 0,
                    width: '100%',
                    height: '80%',
                    backgroundColor: '#05050585',
                    zIndex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column'
                }}>
                    <Box mb={2}>
                        <LockOutlined style={{ color: 'white', width: 150, height: 150 }} />
                    </Box>
                    <Typography variant='h3' fontWeight='bold' style={{ color: 'white' }}>
                        You do not have permission to access any data sheets.
                    </Typography>
                    <Typography variant='h3' fontWeight='bold' style={{ color: 'white' }}>
                        Please contact your Verification Agency to find out more.
                    </Typography>
                </Box>
            }

            <Typography variant='h4' fontWeight='bold' style={{ paddingBottom: "15px" }} ref={importDataRef}>
                Import Data
            </Typography>

            <Grid container spacing={2} component="form" id='MultiPageImportForm' onSubmit={handleSubmit(handleDataSubmit)} >
                <Grid item xs={12}>
                    <Typography variant='body2' color="secondary">
                        {currentUser.isClient
                            ? "Please import a single Excel File with your selected claims. Once imported, please review the claims and submit each element for verification by your Verification Agency."
                            : "Please select a single Excel File to import selected claims. Once imported, your client will need to submit each element to be verified."
                        }
                    </Typography>
                </Grid>

                <Grid item xs={7}>
                    <Box display="flex" justifyContent="space-between" flexDirection="column" height="100%">
                        {dataPagesOptions && (
                            <Box>
                                <AppMultiSelectWithDisabledEntries
                                    disabled={isImportInProgressForScorecard}
                                    control={control}
                                    name="selectedImportTypes"
                                    label="Please select sheet(s) to import data for"
                                    options={dataPagesOptions}
                                    limit={100}
                                    includeSelectAll={true}
                                />
                            </Box>
                        )}
                        <Box mt={1}>
                            <Typography style={{ fontSize: '12px', color: 'gray', fontStyle: 'italic', marginBottom: '2px' }}>
                                *Please note that all imports will overwrite the current content on the system for the sheets you are importing.
                            </Typography>
                            <TooltipLoadingButton buttonText="Begin Import" form="MultiPageImportForm" disabled={isImportInProgressForScorecard || !hasAccess} isLoading={isImporting} showTooltipOnDisabled={true} tooltipTitle={""} />
                        </Box>

                    </Box>
                </Grid>
                <Grid item xs={5}>
                    <AppFileDropzone
                        control={control}
                        name="file"
                        excelOnly={true}
                        width='100%'
                        disabled={isImportInProgressForScorecard}
                        disabledMessage={`Import in progress for "${scorecard.scorecardName}"` }
                    />
                </Grid>
            </Grid>

            <Divider />

            <Grid item xs={12} ref={dataValidationRef}>
                <Typography variant='body1' color="secondary">
                    1. Validating file layout
                </Typography>
                <Typography variant='body2' color="secondary" mb={1}>
                    We'll validate that all sheets and columns in your uploaded file match what's required in our system.
                    We'll let you know if there are missing or incorrectly named sheets and columns to be rectified before we can import your claims.
                </Typography>

                {!importResult && !isImportInProgressForScorecard && !isImporting && !progressData &&
                    defaultInformation()
                }

                {isImporting &&
                    <Grid item xs={12} style={{ backgroundColor: '#CFDADB' }}>
                        <Box sx={{
                            height: '100%',
                            width: '100%',
                            paddingLeft: "15px",
                            paddingTop: "20px",
                            paddingBottom: '20px'
                        }}>
                            <Grid container>
                                <Grid item width="60px">
                                    <InfoOutlined color="info" style={{ width: iconWidth, height: iconHeight }} />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2"
                                        sx={{ mb: 0, paddingTop: '10px', fontWeight: 'regular', color: '#0D3C61' }}
                                        color="secondary">
                                        The structure of your file is being validated
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                }

                {canProcessFile && isFileStructureCorrect && !isImporting &&
                    <Grid item xs={12} style={{ backgroundColor: '#bce2be' }}>
                        <Box sx={{
                            height: '100%',
                            width: '100%',
                            paddingLeft: "15px",
                            paddingTop: "20px",
                            paddingBottom: '20px'
                        }}>
                            <Grid container>
                                <Grid item width="60px">
                                    <CheckCircleOutlineRounded color="success"
                                        style={{ width: iconWidth, height: iconHeight }} />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2"
                                        sx={{ mb: 0, paddingTop: '10px', fontWeight: 'regular', color: '#1E4620' }}
                                        color="secondary">
                                        Your file has a valid structure, please see below for the import status of
                                        your files(s).
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                }

                {importResult && !canProcessFile && !isImporting &&
                    <Grid item xs={12} style={{ backgroundColor: '#fcd3d0' }}>
                        <Box sx={{
                            height: '100%',
                            width: '100%',
                            paddingLeft: "15px",
                            paddingTop: "20px",
                            paddingBottom: '20px'
                        }}>
                            <Grid container>
                                <Grid item width="60px">
                                    <ErrorOutlineOutlined color="error"
                                                               style={{ width: iconWidth, height: iconHeight }} />
                                </Grid>
                                <Grid item xs={6}>
                                    {importResult!.log?.map((errorMessage, index) => {
                                        return (
                                            <Box>
                                                <Typography variant="body2" sx={{ mb: 0, paddingTop: '10px', color: '#621B16' }} color="secondary" key={index}>
                                                    {errorMessage}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                }

                {importResult && !isFileStructureCorrect && !isImporting &&
                    <Grid item xs={12} style={{ backgroundColor: '#fcd3d0' }}>
                        <Box sx={{
                            height: '100%',
                            width: '100%',
                            paddingLeft: "15px",
                            paddingTop: "20px",
                            paddingBottom: '20px'
                        }}>
                            <Grid container>
                                <Grid item width="60px">
                                    <ErrorOutlineOutlined color="error"
                                        style={{ width: iconWidth, height: iconHeight }} />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2"
                                        sx={{ mb: 0, paddingTop: '10px', fontWeight: 'regular', color: '#621B16' }}
                                        color="secondary">
                                        Some sheets in your file are invalid.
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container>
                                <Grid item xs={6}>
                                    <Typography variant="body2" sx={{ mb: 0, paddingTop: '10px', color: '#621B16' }} color="secondary">
                                        The following sheets were invalid:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    {Object.keys(importResult!.additionalInfo).length > 0 &&
                                        <Typography variant="body2"
                                            sx={{ mb: 0, paddingTop: '10px', color: '#621B16' }}
                                            color="secondary">
                                            The following sheets were valid:
                                        </Typography>
                                    }
                                </Grid>
                            </Grid>
                            <Grid container>
                                <Grid item xs={6}>
                                    {importResult!.log?.map((errorMessage, index) => {
                                        const sheetInfo = errorMessage.split('sheet');
                                        const columnInfo = sheetInfo[1].split('column');
                                        return (
                                            <Box>
                                                <Typography variant="caption" sx={{ mb: 0, color: '#621B16' }} key={index}>
                                                    <span style={{ fontStyle: 'italic' }}>{sheetInfo[0]}</span>sheet
                                                    {columnInfo.length > 1 && (
                                                        <>
                                                            <span style={{ fontWeight: 'bold' }}>{columnInfo[0]}</span>column
                                                        </>
                                                    )}
                                                    {columnInfo.length > 1 ? columnInfo[1] : columnInfo[0]}
                                                </Typography>
                                            </Box>
                                        );
                                    })}

                                </Grid>
                                <Grid item xs={6}>
                                    <Box>
                                        <Typography variant="caption"
                                            sx={{ mb: 0, fontStyle: 'italic', color: '#621B16' }}>
                                            {Object.values(importResult!.additionalInfo).join(', ')}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid container style={{ paddingTop: "20px" }}>
                                <Typography color="error" variant="caption" sx={{ mb: 0, fontStyle: 'italic' }}>
                                    Note that the names of sheets and columns must match the template your Verification Agency sent you.
                                </Typography>
                            </Grid>
                            <Grid container style={{ paddingTop: "20px" }}>
                                <Grid item xs={12} >
                                    <MDButton color='error' variant="outlined" sx={{ mr: 1 }}
                                        onClick={() => clearImport()}>
                                        Clear import
                                    </MDButton>
                                    <Button color='primary' variant="contained"
                                        onClick={() => reselectSuccessful(Object.keys(importResult!.additionalInfo).map(Number))}>
                                        Reselect successful sheets
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                }
            </Grid>

            <Divider />

            <Grid item xs={12} ref={inProgressRef}>
                <Typography variant='body1' color="secondary">
                    2. Importing claims per sub-element
                </Typography>
                <Typography variant='body2' color="secondary" mb={1}>
                    {currentUser.isClient
                        ? "We'll import each claim in your file per sub-element so that you can then review and submit to your Verification Agency."
                        : "We'll import each claim in your file per sub-element so that you can then review them."
                    }
                    {importDetailsId &&
                        <Tooltip title={<span>To view more information and/or errors, click <Link
                            to={`/bee/scorecard/${scorecard.scorecardId}/importDetails/${importDetailsId}/${useSignalR}`}>here</Link></span>}>
                            <InfoOutlined color='info' sx={{ ml: 0.5 }} />
                        </Tooltip>
                    }
                </Typography>
                {!progressData &&
                    defaultInformation()
                }
            </Grid>

            {progressData && maxSteps > 0 &&
                <Grid container item style={{ paddingTop: "20px" }}>
                    <Grid item xs={12} style={{ backgroundColor: 'white' }}>
                        <Box sx={{ height: "100%", width: '100%', paddingLeft: "15px", paddingTop: "20px", paddingBottom: "20px" }}>
                            <Grid container spacing={2}>
                                {renderItemsForStep(activeStep)}
                            </Grid>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ width: '100%' }}>
                            <MobileStepper
                                variant="dots"
                                steps={maxSteps}
                                position="static"
                                activeStep={activeStep}
                                sx={{ background: 'white' }}
                                nextButton={
                                    <Button
                                        size="small"
                                        onClick={handleNext}
                                        disabled={activeStep === maxSteps - 1}
                                    >
                                        <KeyboardArrowRight />
                                    </Button>
                                }
                                backButton={
                                    <Button
                                        size="small"
                                        onClick={handleBack}
                                        disabled={activeStep === 0}
                                    >
                                        <KeyboardArrowLeft />
                                    </Button>
                                }
                            />
                        </Box>
                    </Grid>
                </Grid>
            }

            <Divider />

            {currentUser.isClient &&
                <Grid item xs={12}>
                    <Typography variant='body1' color="secondary">
                        3. Submit data pages for verification
                    </Typography>
                    <Typography variant='body2' color="secondary" mb={1}>
                        Once your claims have been imported, you'll need to review and submit each sub-element to your Verification Agency. <br />
                        Use our submissions checklist to see which sub-elements still require your attention.
                    </Typography>
                    <MDButton variant='outlined' color='primary' onClick={() => history.push(`/bee/scorecard/${scorecard.scorecardId}/verificationProcess/elements`)}>
                        View checklist
                    </MDButton>
                </Grid>
            }
        </>
    )
}
